import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Keeps a dashboard's active section in the URL (`?section=orders`).
 *
 * The dashboards used to hold the active tab in plain React state, so it
 * existed nowhere in the browser's history. Opening an order's invoice and
 * pressing Back returned you to `/buyer-dashboard` — a URL that says nothing
 * about which tab you were on — and the dashboard re-mounted on its default
 * section. That's the "it sends me to the profile dashboard instead of my
 * orders" report: the page you came back to genuinely had no memory of it.
 *
 * The one mechanism that did exist (passing `location.state.activeSection`
 * when navigating in) then called `window.history.replaceState({})` on
 * arrival, wiping that state out of the history entry — so even a correctly
 * seeded entry couldn't survive a later Back.
 *
 * Putting the section in the query string fixes both directions at once:
 * switching tabs pushes a real history entry, so Back steps between tabs the
 * way people expect, and any page navigated to from here can return to the
 * exact tab by going back.
 *
 * Drop-in for `useState` — returns the same [value, setter] pair, so existing
 * `setActiveSection(...)` call sites need no changes.
 */
export const useDashboardSection = (defaultSection = 'dashboard') => {
  const navigate = useNavigate();
  const location = useLocation();

  const sectionFromUrl = useCallback(() => {
    const fromQuery = new URLSearchParams(location.search).get('section');
    if (fromQuery) return fromQuery;
    // Still honour the older state-based hand-off so in-app links that pass
    // { state: { activeSection } } keep working.
    if (location.state?.activeSection) return location.state.activeSection;
    return defaultSection;
  }, [location.search, location.state, defaultSection]);

  const [activeSection, setActiveSectionState] = useState(sectionFromUrl);

  // URL -> state. Covers browser Back/Forward, which changes the URL without
  // re-mounting this component.
  useEffect(() => {
    const next = sectionFromUrl();
    setActiveSectionState((current) => (current === next ? current : next));
  }, [sectionFromUrl]);

  // state -> URL. Pushes a history entry so Back returns to the previous tab
  // rather than leaving the dashboard entirely.
  const setActiveSection = useCallback((next) => {
    setActiveSectionState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      if (value !== current) {
        const params = new URLSearchParams(window.location.search);
        params.set('section', value);
        navigate(`${window.location.pathname}?${params.toString()}`, { replace: false });
      }
      return value;
    });
  }, [navigate]);

  return [activeSection, setActiveSection];
};

export default useDashboardSection;
